import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  type BookedFlightItineraryWithDepartureTime,
  Status,
  type Airline,
} from "@b2bportal/air-booking-api";
import { ReservationStateEnum, type Reservation } from "@b2bportal/lodging-api";
import { useExperiment } from "@hopper-b2b/experiments";
import { Trans, useI18nContext } from "@hopper-b2b/i18n";
import { NoPostBookingBanner } from "@hopper-b2b/trips/src/pages/ApacTripsList/components/FlightItineraryDetails/components/NoPostBookingBanner/component";
import { getAirportMap } from "@hopper-b2b/trips/src/pages/TripsList/reducer";
import type {
  FlightInfoDetails,
  IConfirmationNumber,
  MyTripsModalTypes,
  TagInfo,
} from "@hopper-b2b/types";
import { ButtonWrap, Divider, StatusTag } from "@hopper-b2b/ui";
import {
  customFormatDateTime,
  dateStringToNewDate,
  useTenantIcons,
} from "@hopper-b2b/utilities";
import { DateTimeFormatStyle } from "@hopper-i18n/formatter";
import { EXPERIMENTS } from "../../../App";
import Arrow from "../../../assets/client/arrow.svg?react";
import CloseIcon from "../../../assets/client/close.svg?react";
import Exchange from "../../../assets/client/exchange.svg?react";
import { useNubankFeatureFlagsContext } from "../../../utils/featureFlagsContext";
import { copyTextFromWebview } from "../../../utils/nubankHandoff";
import { InsuranceBlurbModal } from "../../InsuranceBlurbModal";
import "./styles.scss";

export interface TripDetailsSummaryProps {
  confirmationNumbers: IConfirmationNumber[];
  onClick?: (modalType: MyTripsModalTypes) => void;
  flightInfoDetails?: FlightInfoDetails;
  hotelDetails?: Reservation;
  title: string;
  titleTag?: TagInfo | undefined;
  isMultiAirline?: boolean;
  flight?: BookedFlightItineraryWithDepartureTime;
  airlineMap?: { [key: string]: Airline | undefined };
  disruptionBanner?: JSX.Element;
}

const TripDetailsSummary = ({
  confirmationNumbers,
  onClick,
  flightInfoDetails,
  hotelDetails,
  title,
  titleTag,
  isMultiAirline,
  flight,
  airlineMap,
  disruptionBanner,
}: TripDetailsSummaryProps) => {
  const { t, brand, language: locale } = useI18nContext();
  const { copy } = useTenantIcons();
  const { enableBookingStatusCallout } = useNubankFeatureFlagsContext();

  const [insuranceCalloutDismissed, setInsuranceCalloutDismissed] =
    useState(false);
  const [insuranceModalOpen, setInsuranceModalOpen] = useState(false);

  const enableTravelInsurance = useExperiment(
    EXPERIMENTS.NUBANK_TRAVEL_INSURANCE
  );

  const airportMap = useSelector(getAirportMap);

  // This is very similar to utils/isFlightDomesticToInternational.ts, however becaue we're in trips we need to do different lookups
  const isDomesticToInternational = useMemo(() => {
    if (!flight) {
      return false;
    }

    // Grab the initial trip slice - regardless of one way or round trip we just need the first slice.
    const travelItineraryInitialSlice =
      flight.bookedItinerary.travelItinerary.slices[0];

    // Get the origin's airport code
    const originLocationAirportCode =
      travelItineraryInitialSlice?.segments[0]?.origin.locationCode;

    // Get the destination's airport code, .length-1 accounts for layovers
    const destinationLocationAirportCode =
      travelItineraryInitialSlice?.segments[
        travelItineraryInitialSlice.segments.length - 1
      ]?.destination.locationCode;

    if (!originLocationAirportCode || !destinationLocationAirportCode) {
      return false;
    }

    // Get the origin/destination's country code, which is in the airportMap
    const originAirport = airportMap[originLocationAirportCode];
    const destinationAirport = airportMap[destinationLocationAirportCode];

    if (
      !originAirport?.geography.countryCode ||
      !destinationAirport?.geography.countryCode
    ) {
      return false;
    }

    const originAirportCountryCode = originAirport.geography.countryCode;
    const destinationAirportCountryCode =
      destinationAirport.geography.countryCode;
    // If both airport country codes are the same, it's not an international flight
    if (originAirportCountryCode === destinationAirportCountryCode) {
      return false;
    }
    // We have two separate countries, if the origin airport matches the preferred country code, the flight begins as domestic
    return originAirportCountryCode === brand?.preferredCountryCode;
  }, [airportMap, brand?.preferredCountryCode, flight]);

  const showInsuranceCallout =
    enableTravelInsurance &&
    isDomesticToInternational &&
    !insuranceCalloutDismissed;

  const tripCategory = flightInfoDetails
    ? flightInfoDetails.roundTrip
      ? t("roundTrip")
      : t("oneWay")
    : hotelDetails
      ? `${
          hotelDetails.lodgingData?.city
            ? `${hotelDetails.lodgingData?.city}, `
            : ""
        }${hotelDetails.lodgingData?.country || ""}`
      : null;

  const dates = flightInfoDetails
    ? `${flightInfoDetails?.startDate} ${
        flightInfoDetails?.endDate ? ` - ${flightInfoDetails?.endDate}` : ""
      }`
    : hotelDetails
      ? `${customFormatDateTime(
          dateStringToNewDate(hotelDetails.checkInDate),
          locale,
          DateTimeFormatStyle.ShortMonthDayShortWeekday,
          brand?.customDateTimeFormats?.tripsList
        )} - ${customFormatDateTime(
          dateStringToNewDate(hotelDetails.checkOutDate),
          locale,
          DateTimeFormatStyle.ShortMonthDayShortWeekday,
          brand?.customDateTimeFormats?.tripsList
        )}`
      : null;

  const bookingCallout = useMemo(() => {
    if (!enableBookingStatusCallout) return null;

    const boldComponents = { bold: <span style={{ fontWeight: 500 }} /> };

    switch (true) {
      case flight?.rawStatus === Status.Canceled ||
        hotelDetails?.state?.ReservationState ===
          ReservationStateEnum.Cancelled:
        return (
          <div className="refund-callout">
            <Trans i18nKey="refundCallout" components={boldComponents} />
          </div>
        );
      case flight?.rawStatus === Status.Booked ||
        flight?.rawStatus === Status.Pending:
        return (
          <div className="ticketing-callout">
            <Trans i18nKey="ticketingCallout" components={boldComponents} />
          </div>
        );
      default:
        return null;
    }
  }, [
    flight?.rawStatus,
    hotelDetails?.state?.ReservationState,
    enableBookingStatusCallout,
  ]);

  const disruptionAlertBanner = disruptionBanner && (
    <div className="disruption-banner-container">{disruptionBanner}</div>
  );

  return (
    <div className="mobile-trip-details-summary slot">
      <div className="mobile-trip-details-summary-header">
        <div className="mobile-trip-details-locations">{title}</div>
        <div className="mobile-trip-details-dates">{dates}</div>
        <div className="mobile-trip-details-trip-category">{tripCategory}</div>
        {titleTag ? (
          <StatusTag
            className="mobile-trip-details-status"
            tagInfo={titleTag!}
          />
        ) : null}
        {bookingCallout}
        {isMultiAirline ? (
          <div className="mobile-trip-details-multi-airline">
            <div className="mobile-trip-details-multi-airline-image-container">
              <Exchange className="mobile-trip-details-multi-airline-image" />
            </div>
            <span className="mobile-trip-details-multi-airline-content">
              {t("multiAirlineTrip")}
            </span>
          </div>
        ) : null}
        {showInsuranceCallout ? (
          <div className="insurance-callout">
            <div className="insurance-callout-content">
              <h4 className="insurance-callout-title">
                {t("insuranceCallout.title")}
              </h4>
              <p className="insurance-callout-description">
                <Trans
                  i18nKey="insuranceCallout.description"
                  components={{
                    bold: <span style={{ fontWeight: 500 }} />,
                  }}
                />
              </p>
              <button
                className="insurance-callout-cta"
                onClick={(e) => {
                  e.stopPropagation();
                  setInsuranceModalOpen(true);
                }}
                type="button"
              >
                <span>{t("insuranceCallout.cta")}</span>
                <Arrow className="arrow-icon" />
              </button>
            </div>
            <button
              className="insurance-callout-dismiss"
              onClick={(e) => {
                e.stopPropagation();
                setInsuranceCalloutDismissed(true);
              }}
              type="button"
              aria-label={t("modalClose.ariaLabel")}
            >
              <CloseIcon className="insurance-callout-dismiss-icon" />
            </button>
          </div>
        ) : null}
        <InsuranceBlurbModal
          open={insuranceModalOpen}
          onClose={() => setInsuranceModalOpen(false)}
        />
        {disruptionAlertBanner}
        {flight?.hasUnsupportedAncillaries && (
          <div className="unsupported-ancillaries-callout">
            {t("unsupportedAncillaries")}
          </div>
        )}
        {flight && airlineMap && (
          <NoPostBookingBanner
            flight={flight}
            airlineMap={airlineMap}
            onOpenConfirmationModal={onClick}
          />
        )}
        <Divider className="mobile-trip-details-divider-reservation-numbers" />
      </div>

      <div className="mobile-trip-details-flight-details" onClick={onClick}>
        <div className="mobile-trip-details-confirmation-numbers-container mobile-trip-details-code-container">
          <h3 className="mobile-trip-details-confirmation-title title">
            {t("confirmationNumber")}
          </h3>
          {confirmationNumbers
            ? confirmationNumbers.map((confirmationNumber, i) => {
                return i !== 0 ? (
                  <div
                    className="mobile-trip-details-confirmation-number code"
                    key={`flight-confirmation-numbers-${i}`}
                  >
                    <p className="flight-confirmation-number">
                      {confirmationNumber.locator}
                    </p>
                    {flightInfoDetails ? (
                      <ButtonWrap
                        className="confirmation-copy-container"
                        onClick={(event) => {
                          event.stopPropagation();
                          copyTextFromWebview(confirmationNumber.locator);
                        }}
                        aria-label={t("copy")}
                      >
                        <img src={copy} className="copy-icon" alt="" />
                      </ButtonWrap>
                    ) : null}
                  </div>
                ) : null;
              })
            : flightInfoDetails?.flightNumber}
        </div>

        <Divider className="mobile-trip-details-divider" />

        <div className="mobile-trip-details-confirmation-number mobile-trip-details-code-container">
          <div className="mobile-trip-details-confirmation-content">
            <h3 className="flight-confirmation-label title">{t("nuTravel")}</h3>
            <p className="flight-confirmation-number code">
              {flightInfoDetails?.confirmationCode ||
                hotelDetails?.reservationId}
            </p>
          </div>
          <ButtonWrap
            className="confirmation-copy-container"
            onClick={() => {
              copyTextFromWebview(
                flightInfoDetails?.confirmationCode ||
                  hotelDetails?.reservationId
              );
            }}
            aria-label={t("copy")}
          >
            <img src={copy} className="copy-icon" alt="" />
          </ButtonWrap>
        </div>
      </div>
    </div>
  );
};

export default TripDetailsSummary;
